import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Search, X } from "lucide-react";

// Fix Leaflet's broken default marker icons when bundled with Vite/Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export interface PickedLocation {
  latitude: number;
  longitude: number;
  /** Display label resolved via Nominatim reverse-geocode (best-effort) */
  label?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

/** Invisible layer that captures map clicks */
const ClickHandler = ({ onPick }: { onPick: (lat: number, lng: number) => void }) => {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
};

/** Flies the map to a new position when flyTo changes */
const FlyTo = ({ flyTo }: { flyTo: [number, number] | null }) => {
  const map = useMap();
  useEffect(() => {
    if (flyTo) map.flyTo(flyTo, 13, { duration: 1 });
  }, [flyTo, map]);
  return null;
};

interface MapLocationPickerProps {
  initial?: { latitude: number; longitude: number } | null;
  onConfirm: (location: PickedLocation) => void;
  title?: string;
  description?: string;
}

export const MapLocationPicker = ({
  initial,
  onConfirm,
  title = "Pick Location",
  description = "Search for a place or click the map to drop a pin.",
}: MapLocationPickerProps) => {
  const [open, setOpen] = useState(false);
  const [selectionTouched, setSelectionTouched] = useState(false);
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(
    initial ? { lat: initial.latitude, lng: initial.longitude } : null
  );
  const [label, setLabel] = useState<string | undefined>();
  const [resolving, setResolving] = useState(false);

  // Search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [flyTo, setFlyTo] = useState<[number, number] | null>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultCenter: [number, number] = [-0.023559, 37.906193];
  const center: [number, number] = pin ? [pin.lat, pin.lng] : defaultCenter;
  const defaultZoom = pin ? 13 : 6;

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setResults([]);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!value.trim()) return;
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=5&countrycodes=ke`,
          { headers: { "Accept-Language": "en" } }
        );
        const data: NominatimResult[] = await res.json();
        setResults(data);
      } catch {
        // Non-fatal
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handleResultSelect = (result: NominatimResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setPin({ lat, lng });
    setLabel(result.display_name);
    setSelectionTouched(true);
    setFlyTo([lat, lng]);
    setQuery(result.display_name);
    setResults([]);
  };

  const handlePick = async (lat: number, lng: number) => {
    setPin({ lat, lng });
    setLabel(undefined);
    setSelectionTouched(true);
    setResults([]);
    setResolving(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      setLabel(data.display_name ?? undefined);
    } catch {
      // Non-fatal
    } finally {
      setResolving(false);
    }
  };

  const handleConfirm = () => {
    if (!pin) return;
    onConfirm({ latitude: pin.lat, longitude: pin.lng, label });
    setOpen(false);
  };

  const handleOpen = () => {
    setPin(initial ? { lat: initial.latitude, lng: initial.longitude } : null);
    setLabel(undefined);
    setSelectionTouched(false);
    setQuery("");
    setResults([]);
    setFlyTo(null);
    setOpen(true);
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={handleOpen} className="gap-2">
        <MapPin className="w-4 h-4" />
        {initial ? "Change Location" : "Pick on Map"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] sm:!max-w-[1200px] p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>{title}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {description}
            </p>
          </DialogHeader>

          {/* Search box */}
          <div className="px-4 pb-2 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9 pr-9"
                placeholder="Search for a location…"
                value={query}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => { setQuery(""); setResults([]); }}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Results dropdown */}
            {(results.length > 0 || searching) && (
              <div className="absolute z-[1000] left-4 right-4 mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {searching && (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>
                )}
                {results.map((r) => (
                  <button
                    key={r.place_id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted truncate border-b last:border-0"
                    onClick={() => handleResultSelect(r)}
                  >
                    {r.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="h-[55vh] min-h-[420px] max-h-[680px] w-full">
            <MapContainer
              key={open ? "open" : "closed"}
              center={center}
              zoom={defaultZoom}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickHandler onPick={handlePick} />
              <FlyTo flyTo={flyTo} />
              {pin && <Marker position={[pin.lat, pin.lng]} />}
            </MapContainer>
          </div>

          {/* Coordinate readout */}
          <div className="px-4 py-3 bg-muted/50 text-sm space-y-1">
            {pin ? (
              <>
                <p className="font-mono text-xs text-muted-foreground">
                  {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
                </p>
                {resolving && <p className="text-muted-foreground">Resolving address…</p>}
                {!resolving && label && <p className="line-clamp-2">{label}</p>}
                {!resolving && !label && <p className="text-muted-foreground">No address found for this point.</p>}
              </>
            ) : (
              <p className="text-muted-foreground">No pin placed — search or click the map.</p>
            )}
          </div>

          <DialogFooter className="px-4 pb-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={!pin || !selectionTouched} onClick={handleConfirm}>
              Use This Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


// Fix Leaflet's broken default marker icons when bundled with Vite/Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export interface PickedLocation {
  latitude: number;
  longitude: number;
  /** Display label resolved via Nominatim reverse-geocode (best-effort) */
  label?: string;
}
